import { ref, computed } from 'vue'
import { defineStore } from 'pinia'
import api from '@/services/axios'
import type { Notification } from '@/types'
import { paginationDefault } from '@/constants'
export const useNotificationStore = defineStore('notification', () => {
  const notifications = ref<Notification[]>([])
  const notification = ref<Notification | null>(null)
  const loading = ref(false)
  const error = ref<string | null | unknown>(null)
  const pagination = ref(paginationDefault)
  const unreadCount = computed(() => notifications.value.filter((notification: Notification) => !notification.read_at).length)

  const fetchNotifications = async (page = 1) => {
    loading.value = true
    error.value = null
    try {
      const response = await api.get(`/api/notifications?page=${page}`)
      notifications.value = response.data.notifications.data
      pagination.value = {
        current_page: response.data.notifications.current_page,
        last_page: response.data.notifications.last_page,
        per_page: response.data.notifications.per_page,
        total: response.data.notifications.total,
        from: response.data.notifications.from,
        to: response.data.notifications.to,
        links: response.data.notifications.links
      }
    } catch (error) {
      error.value = error
    } finally {
      loading.value = false
    }
  }

  const fetchNotification = async (notificationId: number) => {
    loading.value = true
    error.value = null
    try {
      const response = await api.get(`/api/notifications/${notificationId}`)
      notification.value = response.data
    } catch (error) {
      error.value = error
    } finally {
      loading.value = false
    }
  }

  const markAsRead = async (notificationId: number) => {
    loading.value = true
    error.value = null
    try {
      await api.put(`/api/notifications/${notificationId}/read`)
      notifications.value = notifications.value.map((notification: Notification) =>
        notification.id === notificationId ? { ...notification, read_at: new Date().toISOString() } : notification
      )
    } catch (error) {
      error.value = error
    } finally {
      loading.value = false
    }
  }

  const markAllAsRead = async () => {
    loading.value = true
    error.value = null
    try {
      await api.put('/api/notifications/mark-all-as-read');
      notifications.value = notifications.value.map((notification: Notification) => ({ ...notification, read_at: new Date().toISOString() }))
    } catch (error) {
      error.value = error
    } finally {
      loading.value = false
    }
  }

  return { notifications, unreadCount, pagination, fetchNotifications, fetchNotification, markAsRead, markAllAsRead, notification, loading, error }
})
